import React, { useState, useRef } from 'react';
import { Badge, TIER_FROM_LEVEL, TIER_GLOW_COLORS } from 'types/interfaces/badge';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import BadgeTooltip from 'components/BadgeTooltip/BadgeTooltip';

interface DisplayedBadgeInfo {
  badgeId: number;
  badge: Badge;
  earnedAt: string;
}

interface TrophyShelfProps {
  displayedBadges: DisplayedBadgeInfo[];
  isOwnProfile: boolean;
  isSelected: (badgeId: number) => boolean;
  onShowcase: (badgeId: number) => void;
}

const TrophyShelf: React.FC<TrophyShelfProps> = ({
  displayedBadges,
  isOwnProfile,
  isSelected,
  onShowcase,
}) => {
  const [hoveredBadge, setHoveredBadge] = useState<{ badge: Badge; earnedAt?: string } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  if (displayedBadges.length === 0) {
    return null;
  }

  const handleBadgeMouseEnter = (e: React.MouseEvent, db: DisplayedBadgeInfo) => {
    hoverTimeout.current = setTimeout(() => {
      setHoveredBadge({ badge: db.badge, earnedAt: db.earnedAt });
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }, 200);
  };

  const handleBadgeMouseMove = (e: React.MouseEvent) => {
    if (hoveredBadge) {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleBadgeMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setHoveredBadge(null);
  };

  return (
    <>
      <div className="trophy-shelf">
        <div className="trophy-shelf__header">
          <span className="trophy-shelf__label">Trophy Shelf</span>
        </div>

        <div className="trophy-shelf__badges">
          {displayedBadges.map((db) => {
            const tier = TIER_FROM_LEVEL[db.badge.level] || 'bronze';
            const glowColor = TIER_GLOW_COLORS[tier];

            return (
              <div
                key={db.badgeId}
                className="trophy-shelf__pedestal"
                style={{ '--badge-glow': glowColor } as React.CSSProperties}
                onMouseEnter={(e) => handleBadgeMouseEnter(e, db)}
                onMouseMove={handleBadgeMouseMove}
                onMouseLeave={handleBadgeMouseLeave}
              >
                <div className="trophy-shelf__badge-container">
                  <BadgeIcon
                    icon={db.badge.icon}
                    shape={db.badge.shape}
                    tier={tier}
                    size={88}
                    earned
                  />
                </div>
                <div className="trophy-shelf__badge-title">{db.badge.title}</div>
                <div className="trophy-shelf__badge-subtitle">{db.badge.subtitle}</div>
                {isOwnProfile && (
                  <button
                    className={`trophy-shelf__showcase-btn${isSelected(db.badge.id) ? ' trophy-shelf__showcase-btn--active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowcase(db.badge.id);
                    }}
                  >
                    {isSelected(db.badge.id) ? 'Showcased \u2713' : 'Showcase'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {hoveredBadge && (
        <BadgeTooltip
          badge={hoveredBadge.badge}
          earned
          earnedAt={hoveredBadge.earnedAt}
          position={tooltipPosition}
        />
      )}
    </>
  );
};

export default TrophyShelf;
